import { setupProjectSchedule, type ScheduleSetupResult } from './projectScheduleSetup';
import { setupProjectChecklist, type ChecklistSetupResult } from './projectChecklistSetup';

export type PresetInitPhase = 'idle' | 'schedule' | 'checklist' | 'done' | 'error';

export interface ProjectPresetInitOptions {
  projectId: string;
  projectType: string;
  userId: string;
  createDefaultSchedule: boolean;
  createDefaultChecklist: boolean;
  includeDependencyLinks: boolean;
  linkToSchedule?: boolean;
  scheduleTemplateId?: string | null;
  onPhaseChange?: (phase: PresetInitPhase, message: string) => void;
}

export interface ProjectPresetInitResult {
  schedule: ScheduleSetupResult | null;
  checklist: ChecklistSetupResult | null;
  success: boolean;
  message: string;
  warnings: string[];
}

/**
 * Initialize schedule and checklist from presets after project creation.
 * Runs schedule first, then checklist, with progress callbacks.
 */
export async function initializeProjectFromPreset(
  options: ProjectPresetInitOptions
): Promise<ProjectPresetInitResult> {
  const warnings: string[] = [];
  let scheduleResult: ScheduleSetupResult | null = null;
  let checklistResult: ChecklistSetupResult | null = null;

  if (options.createDefaultSchedule) {
    options.onPhaseChange?.('schedule', 'Setting up project schedule...');
    scheduleResult = await setupProjectSchedule({
      projectId: options.projectId,
      projectType: options.projectType,
      createDefaultSchedule: true,
      includeDependencyLinks: options.includeDependencyLinks,
      generateInitialTimeline: false,
      scheduleTemplateId: options.scheduleTemplateId,
    });

    if (scheduleResult.warning) warnings.push(scheduleResult.warning);
    if (!scheduleResult.success && !scheduleResult.alreadyExists) {
      options.onPhaseChange?.('error', scheduleResult.warning || scheduleResult.message);
      return {
        schedule: scheduleResult,
        checklist: null,
        success: false,
        message: scheduleResult.warning || scheduleResult.message,
        warnings,
      };
    }
  }

  if (options.createDefaultChecklist) {
    options.onPhaseChange?.('checklist', 'Setting up project checklist...');
    checklistResult = await setupProjectChecklist({
      projectId: options.projectId,
      projectType: options.projectType,
      createDefaultChecklist: true,
      userId: options.userId,
      linkToSchedule: options.linkToSchedule,
    });

    if (checklistResult.warning) warnings.push(checklistResult.warning);
    if (!checklistResult.success && !checklistResult.alreadyExists) {
      options.onPhaseChange?.('error', checklistResult.error || checklistResult.message);
      return {
        schedule: scheduleResult,
        checklist: checklistResult,
        success: false,
        message: checklistResult.error || checklistResult.message,
        warnings,
      };
    }
  }

  options.onPhaseChange?.('done', 'Project setup complete.');
  return {
    schedule: scheduleResult,
    checklist: checklistResult,
    success: true,
    message: 'Project preset initialization complete.',
    warnings,
  };
}

export async function retryScheduleSetup(
  projectId: string,
  projectType: string,
  includeDependencyLinks = true
): Promise<ScheduleSetupResult> {
  return setupProjectSchedule({
    projectId,
    projectType,
    createDefaultSchedule: true,
    includeDependencyLinks,
    generateInitialTimeline: false,
  });
}

export async function retryChecklistSetup(
  projectId: string,
  projectType: string,
  userId: string
): Promise<ChecklistSetupResult> {
  return setupProjectChecklist({
    projectId,
    projectType,
    createDefaultChecklist: true,
    userId,
  });
}
